import React from "react";
import {Row, Col, Card, Statistic, Spin} from "antd";
import {
    InboxOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    BranchesOutlined,
    CalendarOutlined,
    ThunderboltOutlined,
    EnvironmentOutlined
} from "@ant-design/icons";
import {useObserver} from "mobx-react-lite";
import {isNumeric} from "../../../../../utils/helpers";

const MetricCard = ({title, value, suffix, icon, loading, color = "#1890ff"}) => (
    <Card
        style={{
            height: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}
        bodyStyle={{padding: "16px"}}
        hoverable
    >
        <Statistic
            title={title}
            value={loading ? 0 : value}
            suffix={loading ? <Spin size="small" /> : suffix}
            prefix={React.cloneElement(icon, {style: {color, fontSize: "20px"}})}
            valueStyle={{
                color: loading ? "#ccc" : "#262626",
                fontSize: "18px",
                fontWeight: "600"
            }}
            titleStyle={{
                fontSize: "12px",
                color: "#666",
                marginBottom: "8px"
            }}
        />
    </Card>
);

export const PackageMetricsGrid = ({data, loading}) => {
    const nullObjectCheck = obj => {
        return JSON.stringify(obj) === JSON.stringify({});
    };

    const formatValue = (value, defaultValue = 0) => {
        if (nullObjectCheck(data) || !isNumeric(value)) {
            return defaultValue;
        }
        return parseInt(value);
    };

    const formatPercentage = value => {
        if (nullObjectCheck(data) || !isNumeric(value)) {
            return 0;
        }
        return parseFloat(value).toFixed(1);
    };

    return useObserver(() => (
        <div>
            <h3 style={{marginBottom: "20px", color: "#262626"}}>Genel Paket İstatistikleri</h3>

            <Row gutter={[16, 16]}>
                {/* First Row - Main Metrics */}
                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="Toplam Paket"
                        value={formatValue(data.totalOrderCount, 0)}
                        suffix="adet"
                        icon={<InboxOutlined />}
                        loading={loading}
                        color="#52c41a"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="Ortalama Teslimat Süresi"
                        value={formatValue(data.averageWaitingTimeInQueue, 0)}
                        suffix="dk"
                        icon={<ClockCircleOutlined />}
                        loading={loading}
                        color="#1890ff"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="İptal Edilen Paketler"
                        value={formatValue(data.canceledOrdersCount, 0)}
                        suffix="adet"
                        icon={<CloseCircleOutlined />}
                        loading={loading}
                        color="#f5222d"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="Birleşen Sipariş Oranı"
                        value={formatPercentage(data.orderPerDelivery * 100)}
                        suffix="%"
                        icon={<BranchesOutlined />}
                        loading={loading}
                        color="#722ed1"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="Günlük Ortalama Paket"
                        value={formatValue(data.deliveredOrdersCount, 0)}
                        suffix="adet"
                        icon={<CalendarOutlined />}
                        loading={loading}
                        color="#fa8c16"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="Ortalama Mesafe"
                        value="0.00"
                        suffix="km"
                        icon={<EnvironmentOutlined />}
                        loading={loading}
                        color="#13c2c2"
                    />
                </Col>
            </Row>

            {/* Second Row - Delivery Time Breakdowns */}
            <h3 style={{marginTop: "40px", marginBottom: "20px", color: "#262626"}}>
                Teslimat Süresi Dağılımı
            </h3>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="30 Dakika Altı Teslimatlar"
                        value={formatValue(data.inQueueDeliveryCount, 0)}
                        suffix="adet"
                        icon={<ThunderboltOutlined />}
                        loading={loading}
                        color="#52c41a"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="30-45 Dakika Teslimatlar"
                        value={formatValue(data.wasInQueueDeliveryCount, 0)}
                        suffix="adet"
                        icon={<ClockCircleOutlined />}
                        loading={loading}
                        color="#1890ff"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="45-60 Dakika Teslimatlar"
                        value={formatValue(data.rejectedOrders, 0)}
                        suffix="adet"
                        icon={<ClockCircleOutlined />}
                        loading={loading}
                        color="#fa8c16"
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                    <MetricCard
                        title="60+ Dakika Teslimatlar"
                        value={0}
                        suffix="adet"
                        icon={<ClockCircleOutlined />}
                        loading={loading}
                        color="#f5222d"
                    />
                </Col>
            </Row>
        </div>
    ));
};
